import React, { useRef, useState } from 'react';
import { EDIT_MODE } from '../../../../constant/Safety/Common/mode';
import UtilityBar from '../../Common/UtilityBar';
import SafetyDatagrid from '../../Common/SafetyDatagrid';
import CommonMessageModalFrame from '../../../Common/Modal/Message/Frame';
import ModalHeader from '../../Common/Modal/ModalHeader';
import ModalButton from '../../Common/Modal/ModalButton';
import createChassisColumnDefs from '../../../../util/Safety/Chassis/Generator/createChassisColumnDefs';
import ModalContent from '../Modal/ModalContent';
import useGetListData from '../../../../hook/Safety/Chassis/Get/useGetListData';
import useGetSpecificChassisData from '../../../../hook/Safety/Chassis/Get/useGetSpecificChassisData';
import { ALERT_MODAL_STATUS } from '../../../../constant/Common/Modal/Alert/status';
import useFormController from '../../../../hook/Safety/Chassis/Form/useFormController';
import usePatchActive from '../../../../hook/Safety/Chassis/Patch/usePatchActive';
import usePatchSpecificChassisData from '../../../../hook/Safety/Chassis/Patch/usePatchSpecificChassisData';
import usePostChassisData from '../../../../hook/Safety/Chassis/Post/usePostChassisData';
import useControlLoadingStatus from '../../../../hook/Safety/Common/Loading/useControlLoadingStatus';
import CommonLoading from '../../../Common/Loading';
import CommonAlert from '../../../Common/Modal/Alert';
import createModalDefaultValue from '../../../../util/Safety/Chassis/Generator/createModalDefaultValue';
import saveChassis from '../../../../util/Safety/Chassis/Handler/Save/saveChassis';
import clickOKButton from '../../../../util/Safety/Common/Handler/Save/okButtonHandler';
import useGetGridFilterOption from '../../../../hook/Safety/Common/Option/useGetGridFilterOption';
import checkPermission from '../../../../util/Common/Router/Handler/checkPermission';
import { PERM_SAFETY_CHASSIS_EDIT } from '../../../../constant/Common/Permission';
import safetyChassisKeys from '../../../../hook/Safety/Chassis/keys';

const SafetyChassisContents = () => {
  /** useRef for handling disabled */
  const disabledRef = useRef({ disabled: false, disabledRow: null });

  /** Essential */
  const gridRef = useRef(null);
  const saveButtonRef = useRef(null);
  const [seq, setSeq] = useState(null);
  const isEditable = () =>
    checkPermission({ permission: [PERM_SAFETY_CHASSIS_EDIT] });

  /** Data modal */
  const [isModalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(EDIT_MODE?.add);

  /** Message modal opener */
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertModalStatus, setAlertModalStatus] = useState({
    message: '',
    status: ALERT_MODAL_STATUS?.STABLE,
  });
  const alertDataSetter = { setAlertOpen, setAlertModalStatus };

  /** Grid, Modal data */
  const {
    listData,
    isLoading: loadingGetList,
    isFetching: fetchingGetList,
  } = useGetListData({ ...alertDataSetter });
  const {
    specificData,
    isFetching: fetchingSpecificData,
    isLoading: loadingSpecificData,
    isRefetching: refetchingSpecificData,
    isError: errorSpecificData,
  } = useGetSpecificChassisData({ seq, isModalOpen, ...alertDataSetter });

  /** Form */
  const { formController, formDefaultValue } = useFormController({
    isModalOpen,
    editMode,
    specificData,
  });

  /** Data patcher */
  const { mutate: mutatePatchActive, isPending: pendingPatchActive } =
    usePatchActive({
      gridRef,
      disabledRef,
      setAlertModalStatus,
      setAlertOpen,
    });
  const { mutate: editMutate, isPending: editMutatePending } =
    usePatchSpecificChassisData({
      gridRef,
      setAlertModalStatus,
      setAlertOpen,
      setModalOpen,
    });
  const { mutate: addMutate, isPending: addMutatePending } = usePostChassisData(
    {
      setAlertModalStatus,
      setAlertOpen,
      setModalOpen,
    },
  );

  /** Loading component opener */
  const { isLoadingOpen, setLoadingOpen } = useControlLoadingStatus({
    isFetching: [fetchingSpecificData],
    isLoading: [loadingGetList, loadingSpecificData],
    isPending: [],
  });

  /** Filter option */
  const gridFilterOption = useGetGridFilterOption();
  const { filterDivisionList } = gridFilterOption;

  /** Grid column definition */
  const [columnDefs, setColumnDefs] = useState(
    createChassisColumnDefs({
      mutatePatchActive,
      isEditable: isEditable(),
      disabledRef,
      ...gridFilterOption,
    }),
  );

  return (
    <>
      <div className="pl-[29px] flex flex-col flex-1 pb-8">
        <UtilityBar
          setModalOpen={setModalOpen}
          setEditMode={setEditMode}
          gridRef={gridRef}
          isEditable={isEditable()}
          subPage="CHASSIS"
          queryKeyList={[safetyChassisKeys.listData()]}
        />
        {filterDivisionList?.length > 1 && (
          <SafetyDatagrid
            gridRef={gridRef}
            columnDefs={columnDefs}
            data={listData}
            setSpecificSeq={setSeq}
            setModalOpen={setModalOpen}
            setEditMode={setEditMode}
            preventTargetColumnID={['active']}
          />
        )}
      </div>
      {isModalOpen && !loadingSpecificData && !errorSpecificData && (
        <CommonMessageModalFrame
          isOpen={isModalOpen}
          setOpen={setModalOpen}
          header={<ModalHeader editMode={editMode} title="Chassis" />}
          content={
            <ModalContent
              formController={formController}
              data={formDefaultValue}
              isEditable={isEditable()}
            />
          }
          button={
            <ModalButton
              onClearClick={() => {
                formController?.reset({
                  ...createModalDefaultValue({
                    specificData,
                    editMode,
                  }),
                });
              }}
              onSaveClick={() => {
                formController?.handleSubmit(data =>
                  saveChassis?.({
                    data,
                    editMutate,
                    addMutate,
                    seq,
                    editMode,
                    setAlertModalStatus,
                    setAlertOpen,
                    saveButtonRef,
                  }),
                )();
              }}
              isEditable={isEditable()}
              saveButtonRef={saveButtonRef}
            />
          }
        />
      )}
      {/* Loading */}
      {isLoadingOpen && <CommonLoading isOpen={isLoadingOpen} />}
      {/* Alert */}
      {isAlertOpen &&
        alertModalStatus?.status !== ALERT_MODAL_STATUS?.SUCCESS && (
          <CommonAlert
            isOpen={isAlertOpen}
            setOpen={setAlertOpen}
            status={alertModalStatus?.status}
            content={alertModalStatus?.message}
            onOKButtonClick={() => {
              clickOKButton?.({
                setAlertOpen,
                setModalOpen,
                status: alertModalStatus?.status,
              });
            }}
          />
        )}
    </>
  );
};

export default SafetyChassisContents;
